import { useLayoutEffect, useRef, type MutableRefObject } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { auth, clearCustomer, setCustomer } from '@/redux/ducks/customer';
import { clearUser } from '@/redux/ducks/user';
import {
  captureWebViewFromUrl,
  stripWebAccessTokenFromUrl,
} from '@/hooks/useIsWebView';

const WEB_ACCESS_TOKEN_AUTH_KEY = 'freeflow-web-access-token-auth';


export const WEB_ACCESS_TOKEN_AUTH_TIMEOUT_MS = 35_000;

type WebAccessTokenAuthPendingState = {
  startedAt: number;
};


export function getWebAccessTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('WebAccessToken') ?? params.get('webAccessToken');
}

function setWebAccessTokenAuthPending(): void {
  try {
    const state: WebAccessTokenAuthPendingState = { startedAt: Date.now() };
    sessionStorage.setItem(WEB_ACCESS_TOKEN_AUTH_KEY, JSON.stringify(state));
  } catch {
    /* storage indisponível */
  }
}

export function clearWebAccessTokenAuthPending(): void {
  try {
    sessionStorage.removeItem(WEB_ACCESS_TOKEN_AUTH_KEY);
  } catch {
    /* storage indisponível */
  }
}

function getPendingAuthState(): WebAccessTokenAuthPendingState | null {
  try {
    const raw = sessionStorage.getItem(WEB_ACCESS_TOKEN_AUTH_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as WebAccessTokenAuthPendingState;
    if (typeof parsed?.startedAt !== 'number') {
      clearWebAccessTokenAuthPending();
      return null;
    }

    return parsed;
  } catch {
    clearWebAccessTokenAuthPending();
    return null;
  }
}

function isPendingAuthExpired(startedAt: number): boolean {
  return Date.now() - startedAt > WEB_ACCESS_TOKEN_AUTH_TIMEOUT_MS;
}


if (typeof window !== 'undefined' && getWebAccessTokenFromUrl()) {
  setWebAccessTokenAuthPending();
}


export function isWebAccessTokenAuthPending(): boolean {
  if (getWebAccessTokenFromUrl()) {
    return true;
  }

  const pendingState = getPendingAuthState();
  if (!pendingState) {
    return false;
  }

  if (isPendingAuthExpired(pendingState.startedAt)) {
    clearWebAccessTokenAuthPending();
    return false;
  }

  return true;
}

type AuthEndReason = 'success' | 'failure' | 'timeout';

function handleWebAccessTokenAuthEnd(
  finishedRef: MutableRefObject<boolean>,
  timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>,
  dispatch: ReturnType<typeof useDispatch>,
  navigate: ReturnType<typeof useNavigate>,
  reason: AuthEndReason,
): void {
  if (finishedRef.current) {
    return;
  }

  finishedRef.current = true;

  if (timeoutRef.current) {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }

  clearWebAccessTokenAuthPending();

  if (reason === 'success') {
    return;
  }

  // Libera o spinner. A landing (Index) assume o controle.
  dispatch(setCustomer({ isLoading: false }));
  if (reason === 'failure') {
    navigate('/?auth=1', { replace: true });
  }
}

/**
 * Autentica via WebAccessToken na URL antes das páginas montarem.
 * Preserva webview=true independente da ordem dos query params.
 */
export default function WebAccessTokenAuth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handledRef = useRef(false);
  const finishedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    captureWebViewFromUrl();

    const pendingState = getPendingAuthState();
    if (pendingState && isPendingAuthExpired(pendingState.startedAt)) {
      clearWebAccessTokenAuthPending();
      dispatch(setCustomer({ isLoading: false }));
      return;
    }

    const webAccessToken = getWebAccessTokenFromUrl();

    if (!webAccessToken || handledRef.current) {
      return;
    }

    handledRef.current = true;
    setWebAccessTokenAuthPending();
    stripWebAccessTokenFromUrl();
    dispatch(clearCustomer());
    dispatch(clearUser());
    dispatch(setCustomer({ isLoading: true }));

    timeoutRef.current = setTimeout(() => {
      handleWebAccessTokenAuthEnd(
        finishedRef,
        timeoutRef,
        dispatch,
        navigate,
        'timeout',
      );
    }, WEB_ACCESS_TOKEN_AUTH_TIMEOUT_MS);

    dispatch(
      auth({
        webAccessToken,
        callback: (success) => {
          handleWebAccessTokenAuthEnd(
            finishedRef,
            timeoutRef,
            dispatch,
            navigate,
            success ? 'success' : 'failure',
          );
        },
      }),
    );

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [dispatch, navigate]);

  return null;
}
